import React, { useEffect, useState, useMemo, useCallback } from "react";
import * as store from "../../framework/customStore";
import { useAppSelector } from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import * as workTimeService from "../../services/workTimeBetaService";
import * as dayBookingService from "../../services/dayBookingService";
import * as baseService from "../../services/baseService";
import { WorkTimeList } from "./workTimeList";
import Grid2 from "@mui/material/Unstable_Grid2";
import { DayBookingTypeItemBeta } from "../../models/dayBookingType/dayBookingTypeItemBeta";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import {
    getWorkShiftTimeSlotItems,
    getWorkTimeTypes,
} from "../../services/workTimeBetaService";
import {
    WorkShiftTimeSlotItemBasic,
    getDutyEndingWorktimes,
    getDutyStartingWorktimes,
    getDutyWorkShiftTimeSlots,
} from "../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { Base } from "../../framework/base";
import * as workTimeBetaService from "../../services/workTimeBetaService";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import { Dayjs } from "dayjs";
import { DayBookingItemBeta } from "../../models/dayBooking/dayBookingItemBeta";
import { SalaryPeriodItem } from "../../models/salaryPeriod/salaryPeriodItem";
import { getDayBookingTypes } from "../../services/dayBookingTypeService";
import { useFetch } from "../../hooks/useFetch";
import { WorkTimeType } from "../../models/workShitTimeSlot/workTimeType";
import { HourBookingCategory } from "../../models/workTime/hourBookingCategory";
import { SalaryRowTypeItem } from "../../models/salary/salaryRowTypeItem";
import { getSalaryRowTypeItems } from "../../services/salaryRowTypeService";
import {
    WorkTimeListCategoryColumns,
    WorkTimeListColumn,
    WorkTimeListColumnGroup,
    WorkTimeListGroupedColumns,
    getAbsenceSalaryRowTypeKindCodes,
} from "../../models/workTime/workTimeList";
import { StrMeasureUnit } from "../../models/common/enums";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { WorkTimeListDialog } from "./workTimeListDialog";
import { useAsyncThunkAction } from "../../hooks/useAsyncThunkAction";
import {
    getEmployeeData,
    loadDayBookings,
    loadWorkShifts,
} from "../../store/workTimeListSlice";
import { showApiError } from "../framework/formUtils";
import { MainLayout } from "../layout/mainLayout";
import { WorkTimeFilters } from "./workTimeFilters";
import { useTranslation } from "react-i18next";

export interface IWorkTimeListProp {
    employees: EmployeeListItemDto[]; // Will be needed here and elsewhere
    path: string; // Is needed by child
}

function GetEmployeeIds(
    selectedEmployees: string[] | null,
    allEmployees: EmployeeListItemDto[]
) {
    return allEmployees
        ?.filter(
            (e) =>
                !selectedEmployees ||
                selectedEmployees.length === 0 ||
                selectedEmployees.includes(e.id)
        )
        .map((e) => e.id);
}

export function WorkTimeListBase(props: IWorkTimeListProp) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const [showLoadingIndicator, setShowLoadingIndicator] = useState<boolean>(false);
    const [dayBookingTypes, setDayBookingTypes] =
        useState<DayBookingTypeItemBeta[]>();
    const [workTimeTypes, setWorkTimeTypes] = useState<WorkTimeType[]>();
    const [salaryRowTypes, setSalaryRowTypes] = useState<SalaryRowTypeItem[]>();
    const [columns, setColumns] = useState<WorkTimeListGroupedColumns>();
    const [savingSalaries, setSavingSalaries] = useState(false);
    const [saveDialogState, setSaveDialogState] = useState<
        "closed" | "open" | "saved"
    >("closed");

    const filters = useAppSelector((state) => state.workTimeBeta.filters);
    const fetchFilters = useAppSelector(
        (state) => state.workTimeBeta.fetchFilters
    );
    const hoursBySalaryPeriod = useAppSelector(
        (state) => state.workTimeList.employeeData
    );
    const dataLoading = useAppSelector(
        (state) => state.workTimeList.employeeDataLoading
    );
    const workShiftsLoading = useAppSelector(
        (state) => state.workTimeList.workShiftsLoading
    );
    const dayBookingsLoading = useAppSelector(
        (state) => state.workTimeList.dayBookingsLoading
    );
    const employeeWorkHours = (hoursBySalaryPeriod ?? {})[
        fetchFilters?.selectedSalaryPeriod?.id
    ];

    const workHours = employeeWorkHours?.workHours;
    const workShifts = employeeWorkHours?.workShifts;
    const dayBookingItems = employeeWorkHours?.dayBookings;

    const employeeIds = useMemo(
        () =>
            filters.selectedEmployeeGroup
                ? GetEmployeeIds(filters.selectedEmployees, props.employees)
                : [],
        [
            filters.selectedEmployees,
            filters.selectedEmployeeGroup,
            props.employees,
        ]
    );
    const filteredEmployees = useMemo(
        () => props.employees?.filter((e) => employeeIds.includes(e.id)) ?? [],
        [employeeIds, props.employees]
    );

    const selectedCostCenters = filters.selectedCostCenters?.map((c) => c?.id);
    const shouldFetch =
        fetchFilters?.initialized &&
        !!filters.selectedEmployeeGroup &&
        !!fetchFilters.selectedSalaryPeriod &&
        employeeIds?.length > 0;

    const getEmployeeDataAction = useCallback(
        () => {
            if (shouldFetch) {
                return getEmployeeData({
                    employeeGroupId: filters.selectedEmployeeGroup?.id,
                    employeeIds,
                    salaryPeriodId: fetchFilters.selectedSalaryPeriod?.id,
                    startDate: Base.dayjsToJsonDate(
                        fetchFilters.selectedSalaryPeriod.startDate
                    ),
                    endDate: Base.dayjsToJsonDate(
                        fetchFilters.selectedSalaryPeriod.endDate
                    ),
                    calculateFillingHours: Base.isAfter(
                        Base.dateStartOf(new Date(), "day"),
                        fetchFilters.selectedSalaryPeriod.endDate
                    ),
                });
            }
        },
        [
            employeeIds,
            filters.selectedEmployeeGroup?.id,
            fetchFilters.selectedSalaryPeriod?.id,
            fetchFilters.selectedSalaryPeriod?.startDate,
            fetchFilters.selectedSalaryPeriod?.endDate,
            shouldFetch,
        ]
    );

    const reload = (showLoadingIndicator: boolean) => {
        if (shouldFetch) {
            if (showLoadingIndicator) {
                setShowLoadingIndicator(true);
            }

            store.customStore.dispatch(getEmployeeDataAction() as any);
        }
    };

    useEffect(() => {
        if (!dataLoading && showLoadingIndicator) {
            setShowLoadingIndicator(false);
        }
    }, [dataLoading]);

    useAsyncThunkAction(getEmployeeDataAction, { onError: showApiError }, [
        getEmployeeDataAction,
    ]);

    useFetch(
        () => getDayBookingTypes(),
        {
            onSuccess: (res) =>
                setDayBookingTypes(
                    Base.getTypedArray<DayBookingTypeItemBeta>(
                        res,
                        DayBookingTypeItemBeta
                    )
                ),
        },
        []
    );

    useFetch(
        () => getWorkTimeTypes(),
        {
            onSuccess: (res) => {
                const workTimeTypes = [
                    new WorkTimeType({
                        name: "Ei työvaihetta",
                        workTimeTypeId: "wtt_null",
                        type: 0,
                    }),
                    ...Base.getTypedArray<WorkTimeType>(res, WorkTimeType),
                ];
                setWorkTimeTypes(workTimeTypes);
            },
        },
        []
    );

    useFetch(
        () => getSalaryRowTypeItems(1000, 1, "", "name", true),
        {
            onSuccess: (res) => {
                setSalaryRowTypes(res.items);
            },
        },
        []
    );

    useEffect(() => {
        if (salaryRowTypes && dayBookingTypes) {
            let categoryColumns = WorkTimeListCategoryColumns.flatMap((c) =>
                salaryRowTypes
                    .filter(
                        (t) =>
                            !c.isTotal &&
                            t.hourBookingCategories.includes(c.category) &&
                            !t.hourBookingCategories.includes(
                                c.ignoreIfCategory
                            )
                    )
                    .map(
                        (srt) =>
                            ({
                                id: c.ignoreSalaryRowTypeId
                                    ? c.category.toString()
                                    : srt.id,
                                name: c.label ?? srt.name,
                                measureUnit: srt.measureUnit,
                                decimals: srt.decimals,
                                salaryRowTypeId: c.ignoreSalaryRowTypeId
                                    ? null
                                    : srt.id,
                                hourBookingCategories: srt.hourBookingCategories
                                    .map((cat) => cat.toString())
                                    .filter((cat) =>
                                        (
                                            c.includedCategories ?? [
                                                c.category.toString(),
                                            ]
                                        ).includes(cat)
                                    ),
                                group: c.group,
                            } as WorkTimeListColumn)
                    )
            ).filter(Boolean);

            categoryColumns = [
                ...WorkTimeListCategoryColumns.filter((c) => c.isTotal)
                    .map((c) => {
                        const cols = categoryColumns.filter(
                            (cc) => cc.group === c.group
                        );

                        if (cols.length === 0) {
                            return null;
                        }

                        return {
                            id: `${c.group}-total`,
                            name: c.label.toUpperCase(),
                            measureUnit: cols[0].measureUnit,
                            decimals: cols[0].decimals,
                            group: c.group,
                            isGroupTotal: true,
                            shortName: c.labelShort?.toUpperCase(),
                        } as WorkTimeListColumn;
                    })
                    .filter(Boolean),
                ...categoryColumns,
            ];

            const otherCols = salaryRowTypes
                .filter(
                    (t) =>
                        !categoryColumns.find(
                            (c) =>
                                c.id === t.id ||
                                t.hourBookingCategories
                                    .map((cat) => cat.toString())
                                    .includes(c.id)
                        )
                )
                .map((t) => {
                    const calculateNormal = dayBookingTypes.some(
                        (d) => d.calculateNormal && d.salaryRowTypeId === t.id
                    );
                    return {
                        id: t.id,
                        name: t.name,
                        salaryRowTypeId: t.id,
                        measureUnit: calculateNormal ? StrMeasureUnit.HourFi : t.measureUnit,
                        decimals: t.decimals,
                        hourBookingCategories: calculateNormal
                            ? [HourBookingCategory.DayBookingNormal.toString()]
                            : [],
                        useDayBookingHours: calculateNormal,
                        group: calculateNormal
                            ? WorkTimeListColumnGroup.Paid
                            : getAbsenceSalaryRowTypeKindCodes().includes(
                                t.salaryRowTypeKindCode
                            )
                                ? WorkTimeListColumnGroup.Absence
                                : WorkTimeListColumnGroup.Other,
                    } as WorkTimeListColumn;
                });

            setColumns(
                Base.groupArray([...categoryColumns, ...otherCols], "group")
            );
        }
    }, [salaryRowTypes, dayBookingTypes]);

    function roundHours(salaryPeriod: SalaryPeriodItem) {
        setLoading(true);

        const promises = [];
        const savePromises = [];
        const ownerParameters = new OwnerParameters();
        const shiftSplitDuration =
            ownerParameters.getWorkShiftSplitDurationMinutes();
        const roundingMinutes = ownerParameters.getWorkShiftRoundingMinutes();
        let inProgressWorkShifts = false;
        for (let i = 0; i < filteredEmployees.length; i++) {
            // get workTimes for employee
            promises.push(
                getWorkShiftTimeSlotItems(
                    Base.dayjsToJsonDateTime(salaryPeriod.startDate),
                    Base.dayjsToJsonDateTime(
                        Base.dateAddDays(salaryPeriod.endDate, 1)
                    ),
                    filteredEmployees[i].id
                ).then((workTimes) => {
                    let shouldLoop = true;
                    const toBeSaved = {};
                    while (shouldLoop) {
                        const roundStart = getDutyStartingWorktimes(
                            workTimes.items,
                            shiftSplitDuration
                        );
                        const startDutys = workTimes.items.filter((i) =>
                            roundStart.includes(i.id)
                        );
                        const dutyEnds = {};

                        // Get endDate for each starting duty.
                        for (const startingWorkTime of startDutys) {
                            const dutySlots = getDutyWorkShiftTimeSlots(
                                startingWorkTime,
                                workTimes.items,
                                shiftSplitDuration
                            ).sort((a, b) =>
                                Base.dayjsCompare(b.endDate, a.endDate)
                            );
                            const endDate = dutySlots[0].endDate;
                            if (endDate) {
                                dutyEnds[startingWorkTime.id] =
                                    Base.dateAddMinutes(
                                        endDate,
                                        shiftSplitDuration
                                    );
                            }
                        }

                        const roundEnd = getDutyEndingWorktimes(
                            workTimes.items,
                            shiftSplitDuration
                        );
                        const firstInProgressWorkTime = workTimes.items
                            .filter((i) => !i.endDate)
                            .sort((a, b) =>
                                Base.dayjsCompare(a.startDate, b.startDate)
                            )[0];
                        inProgressWorkShifts =
                            inProgressWorkShifts || !!firstInProgressWorkTime;
                        let newEdits = false;

                        // loop for every workTime
                        for (let j = 0; j < workTimes.items.length; j++) {
                            if (
                                !workTimes.items[j].endDate ||
                                workTimes.items[j].isLocked()
                            ) {
                                continue;
                            }
                            let edited = false;
                            // Don't round if duty is in progress. (There is in progress work time which starts before duty ends).
                            if (
                                roundStart.includes(workTimes.items[j].id) &&
                                (!firstInProgressWorkTime ||
                                    Base.isBefore(
                                        dutyEnds[
                                            workTimes.items[j].id
                                        ] as Dayjs,
                                        firstInProgressWorkTime.startDate
                                    ))
                            ) {
                                const dateWithoutTz =
                                    Base.dateStrToDayjsIgnoreTimezone(
                                        workTimes.items[j].startDate
                                    ).toDate();
                                const roundedMinutes =
                                    Base.roundDateToNearestMinutes(
                                        dateWithoutTz,
                                        roundingMinutes
                                    );
                                if (
                                    roundedMinutes.getTime() !==
                                    dateWithoutTz.getTime()
                                ) {
                                    workTimes.items[j].startDate =
                                        Base.dateToIsoStringAtTimezone(
                                            roundedMinutes,
                                            Base.tzOffsetFromStr(
                                                workTimes.items[j].startDate
                                            )
                                        );
                                    edited = true;
                                    if (
                                        Base.isAfter(
                                            workTimes.items[j].startDate,
                                            workTimes.items[j].endDate
                                        )
                                    ) {
                                        workTimes.items[j].endDate =
                                            workTimes.items[j].startDate;
                                    }
                                }
                            }
                            // Only round if the work time end (plus getWorkShiftSplitDurationMinutes) is before the earliest in progress work time.
                            if (
                                roundEnd.includes(workTimes.items[j].id) &&
                                (!firstInProgressWorkTime ||
                                    Base.isBefore(
                                        Base.dateAddMinutes(
                                            workTimes.items[j].endDate,
                                            ownerParameters.getWorkShiftSplitDurationMinutes()
                                        ),
                                        firstInProgressWorkTime.startDate
                                    ))
                            ) {
                                const dateWithoutTz =
                                    Base.dateStrToDayjsIgnoreTimezone(
                                        workTimes.items[j].endDate
                                    ).toDate();
                                const roundedMinutes =
                                    Base.roundDateToNearestMinutes(
                                        dateWithoutTz,
                                        roundingMinutes
                                    );
                                if (
                                    roundedMinutes.getTime() !==
                                    dateWithoutTz.getTime()
                                ) {
                                    workTimes.items[j].endDate =
                                        Base.dateToIsoStringAtTimezone(
                                            roundedMinutes,
                                            Base.tzOffsetFromStr(
                                                workTimes.items[j].endDate
                                            )
                                        );
                                    edited = true;
                                    if (
                                        Base.isAfter(
                                            workTimes.items[j].startDate,
                                            workTimes.items[j].endDate
                                        )
                                    ) {
                                        workTimes.items[j].startDate =
                                            workTimes.items[j].endDate;
                                    }
                                }
                            }
                            if (edited) {
                                newEdits = true;
                                toBeSaved[workTimes.items[j].id] =
                                    workTimes.items[j];
                            }
                        }

                        if (!newEdits) {
                            shouldLoop = false;
                        }
                    }
                    savePromises.push(
                        workTimeBetaService.saveWorkShiftTimeSlots(
                            Object.values(toBeSaved)
                        )
                    );
                })
            );
        }
        Promise.all(promises)
            .then(() => {
                Promise.all(savePromises)
                    .then(() => reload(true))
                    .catch((error) => {
                        store.customStore.dispatch(
                            storeActions.showErrorMessage(
                                baseService.getErrorMessageFromError(error)
                            )
                        );
                    });
            })
            .catch((error) => {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        baseService.getErrorMessageFromError(error)
                    )
                );
                return null;
            })
            .finally(() => {
                setLoading(false);
                if (inProgressWorkShifts) {
                    store.customStore.dispatch(
                        storeActions.showInfoMessage(
                            t("workTime.workTimeRoundingInfoInProgressWorkShifts")
                        )
                    );
                }
            });
    }

    const acceptDisabled =
        dataLoading || !workHours?.length || selectedCostCenters?.length > 0;

    function acceptVisibleRowsAll() {
        if (!acceptDisabled) {
            setSaveDialogState("open");
        }
    }

    function roundVisibleRowsAll() {
        store.customStore.dispatch(
            storeActions.setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                t("workTime.roundUnlockedWorkShiftsApproveWarning"),
                () => {
                    store.customStore.dispatch(
                        storeActions.clearConfirmation()
                    );
                    roundHours(fetchFilters.selectedSalaryPeriod);
                },
                () => {
                    store.customStore.dispatch(
                        storeActions.clearConfirmation()
                    );
                }
            )
        );
    }

    function toggleLock(
        workTimeListItems: WorkShiftTimeSlotItemBasic[],
        dayBookingItems: DayBookingItemBeta[],
        lock: boolean
    ) {
        setLoading(true);
        const workTimeIds = workTimeListItems.map((x) => x.id);
        const dayBookingIds = dayBookingItems.map((x) => x.id);
        const promises = [];

        if (workTimeIds.length > 0) {
            promises.push(
                lock
                    ? workTimeService.lockWorkTimes(workTimeIds)
                    : workTimeService.unlockWorkTimes(workTimeIds)
            );
        }

        if (dayBookingIds.length > 0) {
            promises.push(
                lock
                    ? dayBookingService.lockDayBookings(dayBookingIds)
                    : dayBookingService.unLockDayBookings(dayBookingIds)
            );
        }

        Promise.all(promises)
            .then(() => {
                const startDate = Base.dayjsToJsonDate(
                    fetchFilters.selectedSalaryPeriod.startDate
                );

                const endDate = Base.dayjsToJsonDate(
                    fetchFilters.selectedSalaryPeriod.endDate
                );

                if (workTimeIds.length > 0)
                    store.customStore.dispatch(
                        loadWorkShifts({
                            employeeIds,
                            salaryPeriodId:
                                fetchFilters.selectedSalaryPeriod.id,
                            startDate,
                            endDate,
                            preserveExisting: true,
                        }) as any
                    );
                if (dayBookingIds.length > 0)
                    store.customStore.dispatch(
                        loadDayBookings({
                            employeeIds,
                            salaryPeriodId:
                                fetchFilters.selectedSalaryPeriod.id,
                            startDate,
                            endDate,
                            preserveExisting: true,
                        }) as any
                    );
            })
            .catch((error) => {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        baseService.getErrorMessageFromError(error)
                    )
                );
            })
            .finally(() => setLoading(false));
    }

    const isLoading =
        loading ||
        showLoadingIndicator ||
        !workHours ||
        workShiftsLoading ||
        !workShifts ||
        dayBookingsLoading ||
        !dayBookingItems ||
        savingSalaries;

    const initialized =
        workTimeTypes &&
        workShifts &&
        dayBookingItems &&
        workHours &&
        columns &&
        shouldFetch;

    return (
        <MainLayout
            noContainer
            topComponent={
                <WorkTimeFilters
                    employees={props.employees}
                    showCostCenters
                    showWorkTimeBottomBar
                    selectedCostCenterIds={selectedCostCenters}
                    acceptAllButtonPress={() => acceptVisibleRowsAll()}
                    roundVisibleRowsAll={() => roundVisibleRowsAll()}
                    acceptDisabled={acceptDisabled}
                />
            }
        >
            <div className="workTimeList">
                <LoadingIndicator loading={isLoading} />
                {initialized && (
                    <Grid2 container justifyContent={"space-between"}>
                        <Grid2
                            xs={12}
                            justifyContent={"space-between"}
                            container
                            spacing={2}
                        >
                            <Grid2
                                xs={12}
                                sx={{
                                    display: "flex",
                                    minHeight: "auto",
                                    maxHeight: "calc(100vh - 220px)"
                                }}
                                id={"workTimeListContainer"}
                            >
                                <WorkTimeList
                                    employees={props.employees ?? []}
                                    employeeIds={employeeIds}
                                    selectedCostCenterIds={selectedCostCenters}
                                    groupedColumns={columns}
                                    workTimeTypes={workTimeTypes}
                                    workShifts={workShifts}
                                    dayBookings={dayBookingItems}
                                    workHours={workHours}
                                    salaryPeriod={fetchFilters.selectedSalaryPeriod}
                                    path={props.path}
                                    lockRows={(items, dayBookings) => toggleLock(items, dayBookings, true)}
                                    unlockRows={(items, dayBookings) => toggleLock(items, dayBookings, false)}
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>
                )}

                <WorkTimeListDialog
                    saveDialogState={saveDialogState}
                    setSaveDialogState={setSaveDialogState}
                    savingSalaries={savingSalaries}
                    setSavingSalaries={setSavingSalaries}
                    workShifts={workShifts}
                    workHours={workHours}
                    filteredEmployees={filteredEmployees}
                    selectedSalaryPeriod={fetchFilters.selectedSalaryPeriod}
                    reload={() => reload(true)}
                />
            </div>
        </MainLayout>
    );
}
